import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { validation } from "../../../../pages/Inventory/Items/schema";
import { formatVatOption } from "../../../../utils/dataTransformer";
import { getItem } from "../../../../features/Inventory/ItemMaster/itemMaster-actions";
import { getAllUom } from "../../../../features/Setting/Uom/uom-actions";
import { itemMasterActions } from "../../../../features/Inventory/ItemMaster/itemMaster-slice";
import { CustomizedBox } from "../../../Custom/CustomizedBox";
import AgGrid from "../../../Table/AgGrid";
import ItemInfo from "../../../../pages/Inventory/Items/ItemInfo";
import ModalUI from "../../../UI/ModalUI";
import DeleteItemConfirmation from "../../../UI/Confirmation/DeleteItemConfirmation";
import CheckedDeliveryOrderTable from "../../../Table/CheckedDeliveryOrderTable";
import CustomizedButton from "../../../Custom/CustomizedButton";
import DeliveryTripFooter from "./DeliveryTripFooter";
import useDeliveryTripColumnDef from "../../../../hooks/Logistic/useDeliveryTripItemList";

const DeliveryTripItemListForm = ({
  control,
  setValue,
  getValues,
  errors,
  viewOnly,
  documentType,
  setHeaderValue,
  openModal,
  setOpenModal,
  deliveryOrderIds,
  setDeliveryOrderIds,
  deliveryOrderIdsSnapshot,
  setDeliveryOrderIdsSnapshot,
  deliveryPlanControl,
  isEdit,
  replaceItemListRef,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { item } = useSelector((state) => state.itemMaster);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openItemDetailModal, setOpenItemDetailModal] = useState(false);
  const [openDeleteItemModal, setOpenDeleteItemModal] = useState(false);
  const [deletedItemKey, setDeletedItemKey] = useState(null);

  const gridRef = useRef(null);
  const footerGridRef = useRef(null);

  const {
    control: itemInfoControl,
    handleSubmit: handleSubmitItemInfo,
    getValues: getItemInfoValues,
    setValue: setItemInfoValue,
    formState: { errors: itemInfoErrors },
    reset: resetItemInfo,
    clearErrors: clearItemInfoErrors,
  } = useForm({
    defaultValues: item,
    resolver: yupResolver(validation),
  });

  useEffect(() => {
    if (item.document_id !== "") {
      Object.entries(item).forEach(([key, value]) => {
        if (key === "sales_vat_type")
          setItemInfoValue(key, formatVatOption(value));
        else if (key === "attribute_list")
          setItemInfoValue(
            key,
            value.map((item) => item.attribute)
          );
        else setItemInfoValue(key, value);
      });
    }
  }, [item, setItemInfoValue]);

  const { fields, replace } = useFieldArray({
    name: "item_list",
    control,
  });

  useLayoutEffect(() => {
    if (replaceItemListRef) {
      replaceItemListRef.current = replace;
    }
  }, [replace, replaceItemListRef]);

  const onRowDragMove = useCallback(
    (event) => {
      const movingNode = event.node;
      const overNode = event.overNode;
      if (movingNode === overNode) return;

      const itemList = getValues("item_list");
      const movingData = movingNode.data;
      const overData = overNode.data;

      const fromIndex = itemList.findIndex(
        (item) =>
          item.uid === movingData.uid &&
          item.do_reference_document_id === movingData.do_reference_document_id
      );
      const toIndex = itemList.findIndex(
        (item) =>
          item.uid === overData.uid &&
          item.do_reference_document_id === overData.do_reference_document_id
      );

      if (fromIndex === -1 || toIndex === -1) return;

      const newList = [...itemList];
      const [moved] = newList.splice(fromIndex, 1);
      newList.splice(toIndex, 0, moved);
      replace(newList);
      gridRef.current.api.clearFocusedCell();
    },
    [getValues, gridRef, replace]
  );

  const getRowId = useCallback((params) => {
    return params.data.uid + (params.data.do_reference_document_id || "");
  }, []);

  const openDeleteItemConfirmationHandler = (uid, doRefId) => {
    setDeletedItemKey({ uid, doRefId });
    setOpenDeleteItemModal(true);
  };

  const closeDeleteItemConfirmationHandler = () => {
    setDeletedItemKey(null);
    setOpenDeleteItemModal(false);
  };

  const handleRemoveItems = () => {
    const currentList = getValues("item_list");
    const newList = currentList.filter(
      (item) =>
        item &&
        item.uid &&
        !(
          item.uid === deletedItemKey.uid &&
          item.do_reference_document_id === deletedItemKey.doRefId
        )
    );
    replace(newList);
    closeDeleteItemConfirmationHandler();
  };

  const handleShowItemModal = (data) => {
    dispatch(getItem({ document_id: data.item_document_id }, enqueueSnackbar));
    dispatch(getAllUom());
    setOpenItemDetailModal(true);
  };

  const handleCloseItemDetailModal = () => {
    searchParams.delete("itemtab");
    setSearchParams(searchParams);
    clearItemInfoErrors();
    setOpenItemDetailModal(false);
    dispatch(itemMasterActions.resetItem());
  };

  const columnDefs = useDeliveryTripColumnDef(
    control,
    errors,
    getValues,
    setValue,
    handleShowItemModal,
    openDeleteItemConfirmationHandler,
    viewOnly,
    false,
    undefined,
    fields,
  );

  return (
    <>
      <CustomizedBox
        margin="0 0rem 2rem 0rem"
        radius="0"
        padding="0"
        boxShadow="0px 2px 7px #E5E5E5"
        clipPath="inset(0 -15px -2px -15px)"
      >
        <AgGrid
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={fields}
          onRowDragMove={onRowDragMove}
          rowDragManaged={true}
          getRowId={getRowId}
          suppressMenu
          disabledSidebar={true}
          disableFloatingFilter
          suppressContextMenu
          autoHeight
          headerClass={["center"]}
          isClientSide
          disableResized
          alignedGrids={
            footerGridRef.current ? [footerGridRef.current] : undefined
          }
          suppressHorizontalScroll
        />
        <DeliveryTripFooter
          control={control}
          errors={errors}
          footerGridRef={footerGridRef}
          getValues={getValues}
          gridRef={gridRef}
          handleShowItemModal={handleCloseItemDetailModal}
          openDeleteItemConfirmationHandler={openDeleteItemConfirmationHandler}
          setValue={setValue}
          viewOnly={viewOnly}
          deliveryPlanControl={deliveryPlanControl}
        />

        {!viewOnly && (
          <CustomizedButton
            sx={{ m: 3 }}
            title="นำเข้าใบส่งของ"
            variant="outlined"
            onClick={() => setOpenModal(true)}
            color="secondary"
          />
        )}
      </CustomizedBox>
      <ModalUI
        open={openItemDetailModal}
        handleClose={handleCloseItemDetailModal}
        navigateTo={`/inventory/items/${item.document_id}`}
        title="รายละเอียดสินค้า"
        fullWidth
      >
        <ItemInfo
          control={itemInfoControl}
          errors={itemInfoErrors}
          getValues={getItemInfoValues}
          handleSubmit={handleSubmitItemInfo}
          reset={resetItemInfo}
          setValue={setItemInfoValue}
          onCancel={handleCloseItemDetailModal}
          viewOnly={true}
        />
      </ModalUI>
      <DeleteItemConfirmation
        documentType={documentType}
        openDeleteItemConfirmation={openDeleteItemModal}
        closeDeleteItemConfirmationHandler={closeDeleteItemConfirmationHandler}
        deleteItemConfirmationAction={handleRemoveItems}
      />
      <CheckedDeliveryOrderTable
        getValues={getValues}
        setValue={setValue}
        replaceItemList={replace}
        setHeaderValue={setHeaderValue}
        openModal={openModal}
        setOpenModal={setOpenModal}
        deliveryOrderIds={deliveryOrderIds}
        setDeliveryOrderIds={setDeliveryOrderIds}
        deliveryOrderIdsSnapshot={deliveryOrderIdsSnapshot}
        setDeliveryOrderIdsSnapshot={setDeliveryOrderIdsSnapshot}
        isEdit={isEdit}
      />
    </>
  );
};

export default DeliveryTripItemListForm;
